import React, { useState, useEffect, useRef } from "react";
import { useUser } from "../../context/UserContext";
import chatService from "../../services/ChatService";
import chatAPI from "../../services/ChatAPI";
import "./Chat.css";

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  messageType: 'text' | 'image' | 'file' | 'audio' | 'video';
  status: 'sent' | 'delivered' | 'read';
  createdAt: Date;
  updatedAt: Date;
  readBy: { [userId: string]: Date };
  isDeleted: boolean;
  replyTo?: {
    messageId: string;
    content: string;
    senderId: string;
  };
}

interface ChatProps {
  matchedUser: {
    name: string;
    fullName: string;
    avatar: string;
    birthCity: string;
  };
  conversationId?: string; // Optional conversation ID passed from parent
  onClose: () => void;
}

const Chat: React.FC<ChatProps> = ({ matchedUser, conversationId: propConversationId, onClose }) => {
  const { user } = useUser();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize chat connection and conversation
  useEffect(() => {
    initializeChat();
    
    return () => {
      // Cleanup on unmount
      if (conversationId) {
        chatService.leaveConversation(conversationId);
      }
      chatService.removeAllListeners();
    };
    // Mount-only: init once; conversationId captured for cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Setup socket listeners when conversation is ready
  useEffect(() => {
    if (conversationId && isConnected) {
      setupSocketListeners();
    }
    // setupSocketListeners is stable for this conversation lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, isConnected]);

  const initializeChat = async () => {
    try {
      setLoading(true);
      
      // Connect to socket
      await chatService.connect();
      setIsConnected(true);
      
      // Setup connection status listener
      chatService.onConnectionStatus(setIsConnected);
      
      // Use conversation ID from props if available, otherwise create one
      if (propConversationId) {
        setConversationId(propConversationId);
        await loadMessages(propConversationId);
      } else {
        // Fallback: create conversation (should not happen in normal flow)
        const userId = matchedUser.name;
        const response = await chatAPI.getOrCreateConversation(userId);
        setConversationId(response.conversation.id);
        await loadMessages(response.conversation.id);
      }
      
    } catch (error: any) {
      console.error("Failed to initialize chat:", error);
      alert("Failed to initialize chat. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId: string) => {
    try {
      const response = await chatAPI.getMessages(convId);
      const messagesData = response.messages.map((msg: any) => {
        // Ensure dates are valid
        const createdAt = msg.createdAt ? new Date(msg.createdAt) : new Date();
        const updatedAt = msg.updatedAt ? new Date(msg.updatedAt) : new Date();
        
        if (isNaN(createdAt.getTime())) {
          console.warn('Invalid createdAt:', msg.createdAt);
        }
        if (isNaN(updatedAt.getTime())) {
          console.warn('Invalid updatedAt:', msg.updatedAt);
        }
        
        return {
          ...msg,
          createdAt: isNaN(createdAt.getTime()) ? new Date() : createdAt,
          updatedAt: isNaN(updatedAt.getTime()) ? new Date() : updatedAt
        };
      });
      setMessages(messagesData);
    } catch (error: any) {
      console.error("Failed to load messages:", error);
    }
  };

  const setupSocketListeners = () => {
    if (!conversationId) return;

    // Join conversation room
    chatService.joinConversation(conversationId);

    // Listen for new messages
    chatService.onNewMessage((data: any) => {
      if (data.conversationId === conversationId) {
        // Ensure dates are valid
        const createdAt = data.message.createdAt ? new Date(data.message.createdAt) : new Date();
        const updatedAt = data.message.updatedAt ? new Date(data.message.updatedAt) : new Date();
        
        if (isNaN(createdAt.getTime())) {
          console.warn('Invalid createdAt in socket message:', data.message.createdAt);
        }
        if (isNaN(updatedAt.getTime())) {
          console.warn('Invalid updatedAt in socket message:', data.message.updatedAt);
        }
        
        const newMessage = {
          ...data.message,
          createdAt: isNaN(createdAt.getTime()) ? new Date() : createdAt,
          updatedAt: isNaN(updatedAt.getTime()) ? new Date() : updatedAt
        };
        setMessages(prev => [...prev, newMessage]);
        scrollToBottom();
      }
    });

    // Listen for typing indicators
    chatService.onUserTyping((data: any) => {
      if (data.conversationId === conversationId) {
        if (data.isTyping) {
          setTypingUsers(prev => [...prev.filter(id => id !== data.userId), data.userId]);
        } else {
          setTypingUsers(prev => prev.filter(id => id !== data.userId));
        }
      }
    });

    // Listen for read receipts
    chatService.onMessagesRead((data: any) => {
      if (data.conversationId === conversationId) {
        setMessages(prev => prev.map(msg => 
          data.messageIds.includes(msg.id) 
            ? { ...msg, status: 'read' as const }
            : msg
        ));
      }
    });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId || !isConnected) return;

    const messageContent = newMessage.trim();
    setNewMessage("");
    setSending(true);

    try {
      // Stop typing indicator
      if (isTyping) {
        chatService.stopTyping(conversationId);
        setIsTyping(false);
      }

      // Send message via socket (preferred) or REST API (fallback)
      if (isConnected) {
        chatService.sendMessage(conversationId, messageContent, 'text');
      } else {
        await chatAPI.sendMessage(conversationId, messageContent, 'text');
      }
      
    } catch (error: any) {
      console.error("Failed to send message:", error);
      alert("Failed to send message. Please try again.");
      setNewMessage(messageContent); // Restore message if sending failed
    } finally {
      setSending(false);
    }
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    
    if (!conversationId || !isConnected) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Start typing indicator
    if (!isTyping && value.trim()) {
      setIsTyping(true);
      chatService.startTyping(conversationId);
    }

    // Stop typing indicator after 1 second of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        chatService.stopTyping(conversationId);
      }
    }, 1000);
  };

  const formatTime = (timestamp: Date) => {
    const date = new Date(timestamp);
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp in formatTime:', timestamp);
      return 'Invalid Date';
    }
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getMessageStatus = (message: Message) => {
    if (message.status === 'read') return '✓✓';
    if (message.status === 'delivered') return '✓✓';
    return '✓';
  };

  return (
    <div className="chat-overlay">
      <div className="chat-container">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="d-flex align-items-center">
            <img
              src={matchedUser.avatar || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRPoAJ0jJpb24AFIkHZ4RfZTXFAHLLjxm33Fg&s"}
              alt={matchedUser.fullName}
              className="chat-avatar"
            />
            <div className="ms-3">
              <h6 className="mb-0 text-black">{matchedUser.fullName}</h6>
              <small className="text-white-50">
                {matchedUser.birthCity}
                <span className={`ms-2 ${isConnected ? 'text-success' : 'text-warning'}`}>
                  {isConnected ? '●' : '●'}
                </span>
              </small>
            </div>
          </div>
          <button className="btn-close btn-close-white" onClick={onClose}></button>
        </div>

        {/* Messages Area */}
        <div className="chat-messages" ref={messageContainerRef}>
          {loading && messages.length === 0 ? (
            <div className="text-center text-muted py-5">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="fas fa-comments fa-3x mb-3 opacity-25"></i>
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            <>
              {messages.map((message) => {
                const isOwnMessage = message.senderId === user?.id;
                return (
                  <div
                    key={message.id}
                    className={`message-wrapper ${isOwnMessage ? "own-message" : "other-message"}`}
                  >
                    <div className={`message-bubble ${isOwnMessage ? "own" : "other"}`}>
                      <p className="mb-1">{message.content}</p>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="message-time">{formatTime(message.createdAt)}</small>
                        {isOwnMessage && (
                          <small className="message-status ms-2">
                            {getMessageStatus(message)}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Typing Indicator */}
              {typingUsers.length > 0 && (
                <div className="message-wrapper other-message">
                  <div className="message-bubble other typing-indicator">
                    <div className="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <small className="text-muted">
                      {typingUsers.length} user{typingUsers.length > 1 ? 's' : ''} typing...
                    </small>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Message Input */}
        <div className="chat-footer">
          <form onSubmit={handleSendMessage} className="d-flex align-items-center w-100">
            <div className="input-group">
              <input
                type="text"
                className="form-control chat-input"
                placeholder={isConnected ? "Type a message..." : "Connecting..."}
                value={newMessage}
                onChange={handleTyping}
                disabled={sending || !isConnected}
              />
              <button
                type="submit"
                className="btn btn-primary chat-send-btn"
                disabled={!newMessage.trim() || sending}
              >
                {sending ? (
                  <span className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Sending...</span>
                  </span>
                ) : (
                  <i className="fas fa-paper-plane"></i>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Chat;

